import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import './App.css';

// Public pages
import LandingPage from './components/public/LandingPage';
import AboutUsPage from './components/public/AboutUsPage';
import ProgramsPage from './components/public/ProgramsPage';
import ProfessionalsPage from './components/public/ProfessionalsPage';
import ProfessionalListingSuccess from './components/public/ProfessionalListingSuccess';
import OfficeBearersPage from './components/public/OfficeBearersPage';
import GalleryPage from './components/public/GalleryPage';
import ContactPage from './components/public/ContactPage';
import MembershipPage from './components/public/MembershipPage';
import MemberLoginPage from './components/auth/MemberLoginPage';
import CompletePaymentPage from './components/auth/CompletePaymentPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import MemberProfile from './pages/MemberProfile/MemberProfile';
import PrivacyPolicyPage from './components/public/PrivacyPolicyPage';
import TermsConditionsPage from './components/public/TermsConditionsPage';

// Admin pages
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import MembersList from './components/admin/MembersList';
import AddMember from './components/admin/AddMember';
import EditMember from './components/admin/EditMember';
import ViewMember from './components/admin/ViewMember';
import ProgramsList from './components/admin/ProgramsList';
import AddProgram from './components/admin/AddProgram';
import EditProgram from './components/admin/EditProgram';
import ViewProgram from './components/admin/ViewProgram';
import PaymentsList from './components/admin/PaymentsList';
import ResourcesList from './components/admin/ResourcesList';
import AddResource from './components/admin/AddResource';
import EditResource from './components/admin/EditResource';
import ViewResource from './components/admin/ViewResource';
import GalleryManager from './components/admin/GalleryManager';
import AdminSettings from './components/admin/AdminSettings';
import AdminProfile from './components/admin/AdminProfile';
import Administrators from './components/admin/Administrators';
import MembershipPlans from './components/admin/MembershipPlansList';
import AddMembershipPlan from './components/admin/AddMembershipPlan';
import ViewMembershipPlan from './components/admin/ViewMembershipPlan';
import EditMembershipPlan from './components/admin/EditMembershipPlan';
import DiscountManager from './components/admin/DiscountsList';
import OfficeBearersList from './components/admin/OfficeBearersList';
import AddOfficeBearer from './components/admin/AddOfficeBearer';
import EditOfficeBearer from './components/admin/EditOfficeBearer';
import ViewOfficeBearer from './components/admin/ViewOfficeBearer';
import DiscountForm from './components/admin/DiscountForm';
import ProfessionalListingsList from './components/admin/ProfessionalListingsList';
import ProfessionalListingPricing from './components/admin/ProfessionalListingPricing';
import ViewProfessionalListing from './components/admin/ViewProfessionalListing';
import AddProfessionalListing from './components/public/AddProfessionalListing';

// Auth guard for admin routes
const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');
  const tokenExpires = localStorage.getItem('adminTokenExpires');
  const isTokenValid = adminToken && tokenExpires && new Date(tokenExpires) > new Date();
  
  if (!isTokenValid) {
    // Clear invalid admin data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminIsSuperAdmin');
    localStorage.removeItem('adminIsViewOnly');
    localStorage.removeItem('adminTokenExpires');
    
    // Redirect to admin login if not authenticated
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Auth guard for member routes - checks authentication and payment status
const MemberRoute = ({ children }) => {
  const memberToken = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
  const paymentRequired = localStorage.getItem('paymentRequired');
  
  // Check if member is authenticated
  if (!memberToken) {
    // Redirect to member login if not authenticated
    return <Navigate to="/member-login" replace />;
  }
  
  // Check if payment is required
  if (paymentRequired === 'true') {
    // Redirect to payment completion page
    return <Navigate to="/complete-payment" replace />;
  }
  
  return children;
};

// Payment gate wrapper - redirects unpaid members to payment page for ALL routes
const PaymentGate = ({ children, allowedPaths = [] }) => {
  const memberToken = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
  const paymentRequired = localStorage.getItem('paymentRequired');
  const currentPath = window.location.pathname;
  
  // If member is logged in and payment is required
  if (memberToken && paymentRequired === 'true') {
    // Allow access only to specific paths
    if (!allowedPaths.includes(currentPath)) {
      return <Navigate to="/complete-payment" replace />;
    }
  }
  
  return children;
};

// Add this new component for handling downloads
const ProgramParticipantsDownload = () => {
  const { id } = useParams();
  
  useEffect(() => {
    window.location.href = `${process.env.REACT_APP_API_URL}/admin/programs/${id}/participants/download`;
  }, [id]);
  
  return <div>Redirecting to download...</div>;
};

function App() {
  // Define paths that unpaid members can access
  const allowedPathsForUnpaid = ['/complete-payment', '/member-login'];
  
  return (
    <Router>
      <Routes>
        {/* Public Routes - wrapped with PaymentGate */}
        <Route path="/" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <LandingPage />
          </PaymentGate>
        } />
        <Route path="/about-us" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <AboutUsPage />
          </PaymentGate>
        } />
        <Route path="/programs" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <ProgramsPage />
          </PaymentGate>
        } />
        <Route path="/professionals" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <ProfessionalsPage />
          </PaymentGate>
        } />
        <Route path="/professionals/add-listing" element={
          <MemberRoute>
            <AddProfessionalListing />
          </MemberRoute>
        } />
        <Route path="/professionals/listing-success" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <ProfessionalListingSuccess />
          </PaymentGate>
        } />
        <Route path="/office-bearers" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <OfficeBearersPage />
          </PaymentGate>
        } />
        <Route path="/gallery" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <GalleryPage />
          </PaymentGate>
        } />
        <Route path="/contact" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <ContactPage />
          </PaymentGate>
        } />
        <Route path="/membership" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <MembershipPage />
          </PaymentGate>
        } />
        <Route path="/member-login" element={<MemberLoginPage />} />
        <Route path="/complete-payment" element={<CompletePaymentPage />} />
        <Route path="/login" element={<Navigate to="/member-login" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/member/profile" element={
          <MemberRoute>
            <MemberProfile />
          </MemberRoute>
        } />
        <Route path="/privacy-policy" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <PrivacyPolicyPage />
          </PaymentGate>
        } />
        <Route path="/terms-conditions" element={
          <PaymentGate allowedPaths={allowedPathsForUnpaid}>
            <TermsConditionsPage />
          </PaymentGate>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } />
        <Route path="/admin/members" element={
          <AdminRoute>
            <MembersList />
          </AdminRoute>
        } />
        <Route path="/admin/members/add" element={
          <AdminRoute>
            <AddMember />
          </AdminRoute>
        } />
        <Route path="/admin/members/:id/edit" element={
          <AdminRoute>
            <EditMember />
          </AdminRoute>
        } />
        <Route path="/admin/members/:id" element={
          <AdminRoute>
            <ViewMember />
          </AdminRoute>
        } />
        <Route path="/admin/programs" element={
          <AdminRoute>
            <ProgramsList />
          </AdminRoute>
        } />
        <Route path="/admin/programs/add" element={
          <AdminRoute>
            <AddProgram />
          </AdminRoute>
        } />
        <Route path="/admin/programs/:id/edit" element={
          <AdminRoute>
            <EditProgram />
          </AdminRoute>
        } />
        <Route path="/admin/programs/:id" element={
          <AdminRoute>
            <ViewProgram />
          </AdminRoute>
        } />
        <Route path="/admin/programs/:id/participants/download" element={
          <AdminRoute>
            <ProgramParticipantsDownload />
          </AdminRoute>
        } />
        <Route path="/admin/payments" element={
          <AdminRoute>
            <PaymentsList />
          </AdminRoute>
        } />
        <Route path="/admin/resources" element={
          <AdminRoute>
            <ResourcesList />
          </AdminRoute>
        } />
        <Route path="/admin/resources/add" element={
          <AdminRoute>
            <AddResource />
          </AdminRoute>
        } />
        <Route path="/admin/resources/:id" element={
          <AdminRoute>
            <ViewResource />
          </AdminRoute>
        } />
        <Route path="/admin/resources/:id/edit" element={
          <AdminRoute>
            <EditResource />
          </AdminRoute>
        } />
        <Route path="/admin/gallery" element={
          <AdminRoute>
            <GalleryManager />
          </AdminRoute>
        } />
        <Route path="/admin/settings" element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        } />
        <Route path="/admin/profile" element={
          <AdminRoute>
            <AdminProfile />
          </AdminRoute>
        } />
        <Route path="/admin/administrators" element={
          <AdminRoute>
            <Administrators />
          </AdminRoute>
        } />
        <Route path="/admin/membership-plans" element={
          <AdminRoute>
            <MembershipPlans />
          </AdminRoute>
        } />
        <Route path="/admin/membership-plans/add" element={
          <AdminRoute>
            <AddMembershipPlan />
          </AdminRoute>
        } />
        <Route path="/admin/membership-plans/:id" element={
          <AdminRoute>
            <ViewMembershipPlan />
          </AdminRoute>
        } />
        <Route path="/admin/membership-plans/:id/edit" element={
          <AdminRoute>
            <EditMembershipPlan />
          </AdminRoute>
        } />
        <Route path="/admin/discounts" element={
          <AdminRoute>
            <DiscountManager />
          </AdminRoute>
        } />
        <Route path="/admin/professional-listings" element={
          <AdminRoute>
            <ProfessionalListingsList />
          </AdminRoute>
        } />
        <Route path="/admin/professional-listings/:id" element={
          <AdminRoute>
            <ViewProfessionalListing />
          </AdminRoute>
        } />
        <Route path="/admin/professional-listing-pricing" element={
          <AdminRoute>
            <ProfessionalListingPricing />
          </AdminRoute>
        } />
        <Route path="/admin/discounts/add" element={
          <AdminRoute>
            <DiscountForm />
          </AdminRoute>
        } />
        <Route path="/admin/discounts/:id/edit" element={
          <AdminRoute>
            <DiscountForm />
          </AdminRoute>
        } />
        <Route path="/admin/office-bearers" element={
          <AdminRoute>
            <OfficeBearersList />
          </AdminRoute>
        } />
        <Route path="/admin/office-bearers/add" element={
          <AdminRoute>
            <AddOfficeBearer />
          </AdminRoute>
        } />
        <Route path="/admin/office-bearers/:id" element={
          <AdminRoute>
            <ViewOfficeBearer />
          </AdminRoute>
        } />
        <Route path="/admin/office-bearers/:id/edit" element={
          <AdminRoute>
            <EditOfficeBearer />
          </AdminRoute>
        } />
        
        {/* Default redirect for admin routes */}
        <Route path="/admin/*" element={<Navigate to="/admin/login" replace />} />
        
        {/* Catch-all route for undefined paths */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
